import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {IconType, IoniconsOutline} from '@pt/pt-models';
import {WidgetTreatmentDataInterestedPreviewDataV0} from './models/widget-treatment-data-interested-preview-data-v0';
import {FormControl, FormGroup} from '@angular/forms';
import {WidgetTreatmentDataInterestedV0Response} from './models/widget-treatment-data-interested-v0-response';
import {WidgetTreatmentDataInterestedV0CellValue} from './models/widget-treatment-data-interested-v0-cell-value';
import {WidgetUtils} from '../../widget.utils';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {WidgetTreatmentDataInterestedV0Value} from './models/widget-treatment-data-interested-v0-value';

@Component({
  selector: 'pt-widget-treatment-data-interested-v0-edit',
  template: `
    <div *ngIf='show && formGroup'>
      <form [formGroup]='formGroup'
            [ngClass]='{"expand": !isCollapsed, "reduce": isCollapsed}'
            class='wtdi-checkbox-container checkbox-form'>

        <!--ROW-->
        <ng-container *ngIf='editableData?.rowCheckbox && editableData?.rowValidator'>
          <ng-container *ngIf='editableData?.rowGroupByCategory; then ROW_CATEGORY; else ROW_LIST'></ng-container>
          <ng-template #ROW_CATEGORY>
            <pt-widget-treatment-data-interested-v0-group-checkbox
              [id]='idPrefix'
              [title]='editableData?.rowTitle'
              [checkboxs]='editableData?.rowCheckbox'
              [controlName]="'row'"
              [validators]='editableData?.rowValidator'
              formControlName='row'>
            </pt-widget-treatment-data-interested-v0-group-checkbox>
          </ng-template>
          <ng-template #ROW_LIST>
            <pt-widget-treatment-data-interested-v0-list-checkbox
              [id]='idPrefix'
              [title]='editableData?.rowTitle'
              [checkboxs]='editableData?.rowCheckbox'
              [controlName]="'row'"
              [validators]='editableData?.rowValidator'
              formControlName='row'>
            </pt-widget-treatment-data-interested-v0-list-checkbox>
          </ng-template>
        </ng-container>

        <!--COL-->
        <ng-container *ngIf='editableData?.colCheckbox && editableData?.colValidator'>
          <ng-container *ngIf='editableData.colGroupByCategory; then COL_CATEGORY; else COL_LIST'></ng-container>

          <ng-template #COL_CATEGORY>
            <pt-widget-treatment-data-interested-v0-group-checkbox
              [id]='idPrefix'
              [title]='editableData?.colTitle'
              [checkboxs]='editableData?.colCheckbox'
              [controlName]="'col'"
              [validators]='editableData?.colValidator'
              formControlName='col'>
            </pt-widget-treatment-data-interested-v0-group-checkbox>
          </ng-template>

          <ng-template #COL_LIST>
            <pt-widget-treatment-data-interested-v0-list-checkbox
              *ngIf='!editableData.colGroupByCategory'
              [id]='idPrefix'
              [title]='editableData?.colTitle'
              [checkboxs]='editableData?.colCheckbox'
              [controlName]="'col'"
              [validators]='editableData?.colValidator'
              formControlName='col'>
            </pt-widget-treatment-data-interested-v0-list-checkbox>
          </ng-template>

        </ng-container>
      </form>

      <div class='toggle-container' (click)='isCollapsed = !isCollapsed'
           ngbTooltip="{{(isCollapsed ? 'LABELS.EXPAND' : 'LABELS.REDUCE') | translate}}">
        <span class='btn btn-sm btn-toggle w-100' [ngClass]="{'flip-icon-toggle': !isCollapsed}">
          <pt-icon [size]='12'
                   [key]='IconArrow'
                   [iconType]='IconType'
          ></pt-icon>
        </span>
      </div>

      <div class='wtdi-table-container'>
        <pt-widget-treatment-data-interested-v0-table
          (toggleCell)='toggleCell($event)'
          [groupByCategory]='editableData?.rowGroupByCategory'
          [row]='row' [col]='col' [cells]='cells' [preview]='false'>
        </pt-widget-treatment-data-interested-v0-table>
      </div>
    </div>
  `,
  styleUrls: [`./widget-treatment-data-interested-v0-edit.component.scss`]
})
export class WidgetTreatmentDataInterestedV0EditComponent implements OnChanges, OnDestroy {

  @Input() show: boolean;
  @Input() editableData: WidgetTreatmentDataInterestedPreviewDataV0;

  @Output() changeValue = new EventEmitter<WidgetTreatmentDataInterestedV0Value>();

  public isCollapsed = false;
  public formGroup: FormGroup;
  public IconType = IconType.IONIC_ICON;
  public IconArrow = IoniconsOutline.arrowDown;
  public idPrefix = WidgetUtils.generateId('widgetTreatmentDataInterestedV0Edit_');
  public row: WidgetTreatmentDataInterestedV0Response[] = [];
  public col: WidgetTreatmentDataInterestedV0Response[] = [];
  public cells: WidgetTreatmentDataInterestedV0CellValue[] = [];
  private colCheckbox: WidgetTreatmentDataInterestedV0Response[];
  private rowCheckbox: WidgetTreatmentDataInterestedV0Response[];
  private actualValue: WidgetTreatmentDataInterestedV0Value;
  private destroy$ = new Subject();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.editableData) {
      this.resetForm();
    }
  }

  toggleCell(cell: WidgetTreatmentDataInterestedV0CellValue) {
    this.cells = this.cells ? JSON.parse(JSON.stringify(this.cells)) : [];
    const localCells = this.cells.filter(c => c.row + '_' + c.col !== cell.row + '_' + cell.col);
    if (localCells?.length == this.cells.length) {
      this.cells.push(cell);
    } else {
      this.cells = localCells;
    }

    this.actualValue = {
      ...this.actualValue,
      cell: this.cells
    };

    this.changeValue.emit(this.actualValue);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private resetForm() {
    this.destroy$.next(true);
    this.isCollapsed = false;

    this.formGroup = new FormGroup({
      row: new FormControl(),
      col: new FormControl()
    });

    this.rowCheckbox = this.editableData?.rowCheckbox;
    this.colCheckbox = this.editableData?.colCheckbox;

    this.setTableRow(this.editableData?.row.map(r => r.uuid));
    this.setTableCol(this.editableData?.col.map(c => c.uuid));
    this.setTableCells(this.editableData?.cells);

    this.actualValue = {
      row: this.editableData?.row.map(r => r.uuid),
      col: this.editableData?.col.map(c => c.uuid),
      cell: this.editableData?.cells
    };

    this.formGroup.controls.row.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((row: string[]) => {
      this.setTableRow(row);
      this.setTableCells(this.cells);

      this.actualValue = {
        ...this.actualValue,
        row: row,
        cell: this.cells
      };

      this.changeValue.emit(this.actualValue);
    });

    this.formGroup.controls.col.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((col: string[]) => {
      this.setTableCol(col);
      this.setTableCells(this.cells);

      this.actualValue = {
        ...this.actualValue,
        col: col,
        cell: this.cells
      };

      this.changeValue.emit(this.actualValue);
    });
  }

  private setTableRow(row: string[]) {
    this.row = [];
    if (row?.length > 0 && this.rowCheckbox?.length > 0) {
      this.rowCheckbox.forEach((checkbox) => {
        if (row.includes(checkbox.uuid)) {
          this.row.push({
            uuid: checkbox.uuid,
            label: checkbox.label,
            help: checkbox.help,
            category: checkbox.category
          });
        }
      });
    }
  }

  private setTableCol(col: string[]) {
    this.col = [];
    if (col?.length > 0 && this.colCheckbox?.length > 0) {
      this.colCheckbox.forEach((checkbox) => {
        if (col.includes(checkbox.uuid)) {
          this.col.push({
            uuid: checkbox.uuid,
            label: checkbox.label,
            help: checkbox.help,
            category: checkbox.category
          });
        }
      });
    }
  }

  private setTableCells(cells: WidgetTreatmentDataInterestedV0CellValue[]) {
    this.cells = cells ? JSON.parse(JSON.stringify(cells)) : null;
  }

}
